const CACHE_PREFIX = "landing_api_cache_v1_";
const FRESH_FOR = 30 * 24 * 60 * 60 * 1000;
const STALE_FOR = 90 * 24 * 60 * 60 * 1000;

const memoryCache = new Map();
const pendingRequests = new Map();

function stableSerialize(value) {
  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(",")}]`;
  }
  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

function hash(value) {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return (result >>> 0).toString(16);
}

export function buildCacheKey({ url, method, payload }) {
  const language = localStorage.getItem("selectedLanguage") || "vi";
  return hash(stableSerialize({ url, method, payload, language }));
}

function readStorage(key) {
  try {
    const stored = localStorage.getItem(`${CACHE_PREFIX}${key}`);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    return null;
  }
}

function writeStorage(key, entry) {
  try {
    localStorage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(entry));
  } catch (error) {
    if (error) {
      return;
    }
  }
}

function readEntry(key) {
  const entry = memoryCache.get(key) || readStorage(key);
  if (entry) {
    memoryCache.set(key, entry);
  }
  return entry;
}

function read(key, maxAge, allowInvalidated) {
  const entry = readEntry(key);
  if (!entry || !entry.response || !entry.cachedAt) {
    return null;
  }
  if (!allowInvalidated && entry.invalidated) {
    return null;
  }
  if (Date.now() - entry.cachedAt > maxAge) {
    return null;
  }
  return entry.response;
}

export function readFresh(key) {
  return read(key, FRESH_FOR, false);
}

export function readStale(key) {
  return read(key, STALE_FOR, true);
}

export function writeResponse(key, response) {
  const entry = { cachedAt: Date.now(), invalidated: false, response };
  memoryCache.set(key, entry);
  writeStorage(key, entry);
}

export async function requestWithCache({ key, request, isSuccess, forceRefresh = false }) {
  const fresh = forceRefresh ? null : readFresh(key);
  if (fresh) {
    return fresh;
  }

  if (pendingRequests.has(key)) {
    return pendingRequests.get(key);
  }

  const pending = Promise.resolve()
    .then(request)
    .then((response) => {
      if (isSuccess(response)) {
        writeResponse(key, response);
        return response;
      }
      return readStale(key) || response;
    })
    .catch((error) => readStale(key) || Promise.reject(error))
    .finally(() => pendingRequests.delete(key));

  pendingRequests.set(key, pending);
  return pending;
}
